import type { Cell } from '@mss_creator/shared';

/**
 * Context-staleness rules (2026-08-26 context/digest design doc, D25 + D26).
 *
 * `matrices.contextUpdatedAt` moves on *any* change to `sujet`, `contexte`,
 * the context file set, a digest or the synthesis — deliberately one single
 * timestamp, so the user never has to reason about *which kind* of change
 * happened. Every cell whose **current** version was created before that
 * instant is therefore potentially out of date, and carries a discreet marker
 * in the grid.
 *
 * D26 makes the whole thing refermable: dismissing the matrix-level banner
 * persists `viewState.dismissedContextChangeAt`, and that suppresses the
 * banner *and the markers* until the next context change pushes
 * `contextUpdatedAt` past the dismissal.
 *
 * These helpers are the single place that comparison lives, so the route, the
 * banner and the grid can never drift apart on the boundary conditions.
 */

/**
 * Parse an ISO-8601 instant to epoch milliseconds, or `null` when it is not a
 * usable date.
 *
 * Parsing rather than comparing the strings is load-bearing: both timestamps
 * are ISO datetimes that may legitimately carry *different* UTC offsets (the
 * backend writes `Z`, but nothing in the schema forbids `+02:00`), and
 * lexicographic order on such strings does not match chronological order.
 */
function toEpoch(iso: string | null | undefined): number | null {
  if (iso === null || iso === undefined) return null;
  const epoch = Date.parse(iso);
  return Number.isNaN(epoch) ? null : epoch;
}

/**
 * True when the matrix has a context change the user has not yet dismissed —
 * i.e. when the banner and the per-cell markers should be visible at all.
 *
 * - `contextUpdatedAt === null` means nothing has changed since generation (or
 *   nothing was ever generated): there is no staleness to report.
 * - No dismissal recorded (`null`/`undefined`, the shape every `viewState`
 *   written before D26 has) means the change is unacknowledged.
 * - Otherwise the banner returns only when the change is *strictly* newer than
 *   the dismissal (D26: "réapparaît dès que
 *   `contextUpdatedAt > dismissedContextChangeAt`"). Equality is the state
 *   right after a dismissal, so it must stay hidden.
 *
 * An unparseable `contextUpdatedAt` is treated as "no context change": we
 * cannot place it on the timeline, and a banner nobody can ever dismiss (its
 * dismissal timestamp would never compare) is worse than a missing one.
 */
export function hasUndismissedContextChange(
  contextUpdatedAt: string | null,
  dismissedContextChangeAt: string | null | undefined,
): boolean {
  const contextEpoch = toEpoch(contextUpdatedAt);
  if (contextEpoch === null) return false;
  const dismissedEpoch = toEpoch(dismissedContextChangeAt);
  if (dismissedEpoch === null) return true;
  return contextEpoch > dismissedEpoch;
}

/**
 * The ids of the cells whose current version predates the matrix's last
 * context change (D25), in the order they appear in `cells`. Empty whenever
 * {@link hasUndismissedContextChange} is false, so a dismissal wipes the
 * markers with no extra branch at the call site.
 *
 * Two cases deliberately count as *not* stale:
 *
 * - `currentVersionId === null` — the cell has never been generated/seeded, so
 *   there is no content that could be out of date.
 * - `currentVersionId` points at a version that is absent from `versions` (a
 *   truncated payload, or a version list the client has not refetched yet).
 *   The cell's age is then simply unknown, and the grid renders no content for
 *   it either way, so we stay silent rather than throw or guess. Marking it
 *   would put a warning on a cell the user cannot even read.
 *
 * A version whose `createdAt` is *exactly* `contextUpdatedAt` is not stale
 * either: only strictly earlier counts. That matters because a regeneration
 * triggered by the very context change being reported can land on the same
 * millisecond, and flagging its output as predating itself would be absurd.
 */
export function staleCellIds(
  cells: Cell[],
  contextUpdatedAt: string | null,
  dismissedContextChangeAt: string | null | undefined,
): string[] {
  if (!hasUndismissedContextChange(contextUpdatedAt, dismissedContextChangeAt)) return [];
  const contextEpoch = toEpoch(contextUpdatedAt);
  // Unreachable in practice — `hasUndismissedContextChange` already rejected
  // an unparseable/absent value — but it keeps `contextEpoch` non-nullable
  // without a non-null assertion.
  if (contextEpoch === null) return [];

  const stale: string[] = [];
  for (const cell of cells) {
    const currentVersionId = cell.currentVersionId;
    if (currentVersionId === null) continue;
    const current = cell.versions.find((version) => version.id === currentVersionId);
    if (current === undefined) continue;
    const createdAtEpoch = toEpoch(current.createdAt);
    if (createdAtEpoch === null) continue;
    if (createdAtEpoch < contextEpoch) stale.push(cell.id);
  }
  return stale;
}
